import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { InputOtp } from 'primereact/inputotp';
import { verifyLoginOTP, storeAuthData, resendLoginOTP } from '../services/authService';
import Navbar from "../components/landingpage/sections/Navbar";

const EmailOtp = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [otp, setOtp] = useState('');
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [timer, setTimer] = useState(600); // 10 minutes
    const [loginMethod, setLoginMethod] = useState('');
    const [identifier, setIdentifier] = useState('');
    const [canResend, setCanResend] = useState(false);

    useEffect(() => {
        if (location.state) {
            if (location.state.userEmail) {
                setLoginMethod('email');
                setIdentifier(location.state.userEmail);
            } else if (location.state.userPhone) {
                setLoginMethod('phone');
                setIdentifier(location.state.userPhone);
            }
        } else {
            navigate(loginMethod === 'email' ? '/email-otp' : '/');
        }
    }, [location, navigate, loginMethod]);

    // Timer countdown effect
    useEffect(() => {
        let interval;
        if (timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else {
            setCanResend(true);
        }
        return () => clearInterval(interval);
    }, [timer]);

    // Format time to MM:SS
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        if (otp.length !== 6) {
            setError('Please enter a valid 6-digit OTP');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const verificationData = {
                otpCode: otp,
                userPassword: location.state.userPassword
            };

            let userEmail = '';

            if (loginMethod === 'email') {
                verificationData.userEmail = location.state.userEmail;
                userEmail = location.state.userEmail;
            } else {
                verificationData.userPhone = location.state.userPhone;
                userEmail = location.state.userEmail;
            }

            const response = await verifyLoginOTP(verificationData);
            console.log('OTP VERIFICATION RESPONSE:', response);

            if (response.success) {
                const enhancedUser = {
                    ...response.data.user,
                    email: userEmail,
                    userEmail: userEmail
                };

                storeAuthData(response.data.token, enhancedUser);
                console.log('Login successful, enhanced data stored', enhancedUser);

                // CHECK FOR PENDING BOOKING - ADD THIS PART
                const pendingBooking = localStorage.getItem('pendingBooking');

                if (pendingBooking) {
                    // Redirect to booking page with stored data
                    const bookingData = JSON.parse(pendingBooking);
                    const bookingParams = new URLSearchParams({
                        celebId: bookingData.celebId,
                        name: encodeURIComponent(bookingData.name),
                        category: encodeURIComponent(bookingData.category),
                        image: encodeURIComponent(bookingData.image),
                        price: bookingData.price,
                        shoutoutPricing: JSON.stringify(bookingData.shoutoutPricing || []) // No encodeURIComponent here
                    });

                    // Clear the pending booking
                    localStorage.removeItem('pendingBooking');

                    // Redirect to booking page
                    window.location.href = `/booking?${bookingParams.toString()}`;
                    return;
                }

                // No pending booking - go to normal dashboard
                if (enhancedUser.userType === 'celeb') {
                    window.location.href = '/dashboard';
                } else {
                    window.location.href = '/search-celeb';
                }
            }
        } catch (err) {
            setError(err.message || 'OTP verification failed. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleResendOtp = async () => {
        if (!canResend) return;

        setIsResending(true);
        setError(null);

        try {
            // Prepare data for resend OTP
            const resendData = {
                userPassword: location.state.userPassword
            };

            if (loginMethod === 'email') {
                resendData.userEmail = location.state.userEmail;
            } else {
                resendData.userPhone = location.state.userPhone;
            }

            // Call your resend OTP API
            const response = await resendLoginOTP(resendData);

            if (response.success) {
                setTimer(600); // Reset to 10 minutes
                setCanResend(false);
                setOtp(''); // Clear previous OTP
                alert('OTP has been resent to your email');
            } else {
                setError('Failed to resend OTP. Please try again.');
            }
        } catch (err) {
            setError('Failed to resend OTP. Please try again.');
        } finally {
            setIsResending(false);
        }
    };

    const maskedIdentifier = identifier ?
        loginMethod === 'email'
            ? identifier.replace(/(.{2})(.*)(?=@)/, (match, p1, p2) => p1 + '*'.repeat(p2.length))
            : identifier.replace(/(\d{3})\d+(\d{4})/, '$1****$2')
        : '';

    return (
        <>
            <Navbar {...props} />
            {/* Page Content */}
            <div className="login-content-info">
                <div className="container">
                    {/* Login OTP Verification */}
                    <div className="row justify-content-center">
                        <div className="col-lg-5 col-md-6">
                            <div className="account-content">
                                <div className="account-info">
                                    <div className="login-verify-img">
                                        <i className="isax isax-sms" />
                                    </div>
                                    <div className="login-title">
                                        <h3>OTP Verification</h3>
                                        <p className="mb-0">
                                            OTP sent to your {loginMethod === 'email' ? 'Email Address' : 'account email'}: {maskedIdentifier}
                                        </p>
                                    </div>

                                    {error && (
                                        <div className="alert alert-danger mb-4">
                                            <div className="d-flex align-items-center">
                                                <i className="fas fa-exclamation-triangle me-2"></i>
                                                <div>
                                                    <strong>Verification Error!</strong>
                                                    <div className="small">{error}</div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <form onSubmit={handleVerifyOtp}>
                                        <div className="otp-box">
                                            <div className="mb-3">
                                                <InputOtp
                                                    value={otp}
                                                    onChange={(e) => setOtp(e.value)}
                                                    length={6}
                                                    integerOnly
                                                    className="justify-content-center"
                                                />
                                            </div>
                                        </div>
                                        <div className="mb-3">
                                            <div className="otp-info">
                                                <div className="otp-code">
                                                    <p>
                                                        Didn't receive OTP code?{" "}
                                                        <Link
                                                            to="#"
                                                            onClick={handleResendOtp}
                                                            className={!canResend || isResending ? 'text-muted' : 'text-primary'}
                                                            style={{
                                                                pointerEvents: !canResend || isResending ? 'none' : 'auto',
                                                                textDecoration: 'none'
                                                            }}
                                                        >
                                                            {isResending ? 'Resending...' : 'Resend Code'}
                                                        </Link>
                                                    </p>
                                                </div>
                                                <div className="otp-sec">
                                                    <p>
                                                        <i className="isax isax-clock" />
                                                        {formatTime(timer)} mins
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="reset-btn">
                                            <button
                                                type="submit"
                                                className="btn btn-primary-gradient w-100"
                                                disabled={isLoading || otp.length !== 6}
                                            >
                                                {isLoading ? 'Verifying...' : 'Verify OTP'}
                                            </button>
                                        </div>
                                    </form>

                                    {timer === 0 && (
                                        <div className="alert alert-warning mt-3">
                                            OTP has expired. Please request a new one.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* /Login OTP Verification */}
                </div>
            </div>
            {/* /Page Content */}
        </>
    );
};

export default EmailOtp;
